/**
 * FirmenStammdaten - the H2 card on the Kunde Übersicht.
 *
 * WHAT H2 ASKS FOR (Stefan, verbatim in the build plan's Appendix A2):
 *   "Komplettierung der Stammdaten in Detailansicht des Kunden «Übersicht» (siehe Prototyp
 *    und Konzept)."
 *
 * The Übersicht carried KPIs and a status, and none of the company's actual master data:
 * no address, no UID, no legal form, no canton. Those columns exist on `firma` and
 * `GET /firms/:id` already returns them behind the blacklist gate, so this is a read of an
 * endpoint that was already there rather than anything new.
 *
 * D1-14 EDIT: the card is now also editable in place. The pencil top-right (shown only to a
 * caller with crm.write) turns the rows into inputs; Speichern PUTs to /firms/:id. A field the
 * broker changes is marked broker-overridden on the server, so the next Handelsregister
 * enrichment will NOT overwrite it — the whole point of the edit sticking.
 *
 * EA task ea/enrich-refresh-button: a RefreshCw icon button next to the enrichment-status label
 * lets the broker manually re-run the register enrichment (POST /firms/:id/enrich, reason=manual
 * → force=true) instead of waiting for the nightly sweep — to refresh stale data or retry a
 * failed/never-matched lookup. The endpoint is async (202 = enqueued), so the button polls
 * firms.get() every ~1.5s for up to ~15s until zefix_status settles, then diffs the
 * enrichment-touched fields against a pre-click snapshot and shows what changed. Server-side
 * rate limiting (apps/api/src/modules/firms/firm-enrich.rate-limiter.ts) enforces a per-firm 30s
 * cooldown + a per-user 20/min cap + an "already running/pending" guard; the button mirrors the
 * cooldown client-side (disabled + countdown) and surfaces 429/422 from ApiError.
 *
 * WHY THIS CARD IS THE PAYOFF FOR D1
 *
 * The register enrichment fills exactly these fields, and until now there was nowhere in the
 * app to see the result. A broker could not tell whether a company had been enriched at all.
 * So this card also surfaces the enrichment STATE honestly: "aus dem Handelsregister
 * übernommen" versus "noch nicht abgeglichen", instead of showing empty fields that look like
 * missing data when they are really a pending lookup.
 *
 * BLACKLIST: `GET /firms/:id` answers a restricted caller with `{ id, name, restricted: true }`
 * and nothing else (D-NAME-ONLY-200-NOT-403). The card renders that state rather than empty
 * fields, because "you may not see this" and "this is not filled in" are different facts.
 *
 * P2-8 (spec-part2-stefan-delta.md §3.2, module p2-customer-stammdaten-web) adds the customer
 * Stammdaten expansion in read mode too, split into the same two groups the edit form uses:
 * an "Automatisch ermittelt" group (NOGA-Code, Branche, Gründungsjahr, Geschäftstätigkeit,
 * MwSt-Nummer, MwSt-pflichtig — each carries the same small "Auto" badge as a per-field
 * affordance, distinct from the card-level enrichment banner above) and a "Manuell erfasst"
 * group (Jahresumsatz, Kunde seit, Referenzwährung, Vorsteuerabzugsberechtigt, Adresszusatz —
 * no badge, since manual is the default assumption for anything not marked "Auto").
 *
 * [Source: 2026-08-11_stefan-feedback-build-plan.md] (H2; Appendix A2 Stefan verbatim)
 * [Source: apps/web/src/features/entity-detail/components/FirmStammdatenForm.tsx] (edit form)
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §3.2] (P2-8 auto vs manual table)
 */

import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Loader2,
  Pencil,
  RefreshCw,
  ShieldOff,
  Sparkles,
  X,
} from 'lucide-react';
import { apiClient, ApiError } from '../../../lib/api-client';
import type { FirmDetailWire } from '../../../lib/api-client';
import { Button } from '../../administration/components';
import { dateShortDe } from '../../../lib/format';
import {
  FirmStammdatenForm,
  draftFromWire,
  draftToPatch,
  draftUnchanged,
  referenceCurrencyInvalid,
  annualRevenueInvalid,
  type FirmDraft,
} from './FirmStammdatenForm';

type FirmDetail = FirmDetailWire;

/** German labels for the legal-form enum as it is stored. */
const LEGAL_FORM_LABEL: Record<string, string> = {
  ag: 'Aktiengesellschaft (AG)',
  gmbh: 'GmbH',
  eg: 'Genossenschaft',
  einzelfirma: 'Einzelfirma',
  verein: 'Verein',
  stiftung: 'Stiftung',
  other: 'Andere',
};

/** What the register lookup has done for this firm, in words a broker can act on. */
function enrichmentLabel(status: string | undefined): string {
  switch (status) {
    case 'done':
      return 'Aus dem Handelsregister übernommen';
    case 'pending':
      return 'Noch nicht abgeglichen';
    case 'in_progress':
      return 'Abgleich läuft';
    case 'failed':
      return 'Abgleich fehlgeschlagen';
    default:
      return 'Unbekannt';
  }
}

/** P2-8: 'Ja' / 'Nein' / '-' for the tri-state boolean fields (mwst_pflichtig, input_tax_deductible). */
function triStateLabel(v: boolean | null | undefined): string | null {
  if (v === true) return 'Ja';
  if (v === false) return 'Nein';
  return null;
}

/**
 * P2-8: money grouped with apostrophe thousands (matches `chf()` in lib/format.ts) plus the
 * firm's own `reference_currency` — NOT hardcoded 'CHF', since a firm may set a different one.
 */
function formatMoney(v: string | null | undefined, currency: string): string | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const [int, dec] = Math.abs(n).toFixed(2).split('.');
  const grouped = int!.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${n < 0 ? '-' : ''}${grouped}.${dec} ${currency}`;
}

/** P2-8: the small "Auto" badge that marks a field as register-derived (broker-overridable). */
function AutoBadge() {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground"
      title="Automatisch aus dem Handelsregister ermittelt, kann überschrieben werden."
    >
      <Sparkles className="size-2.5" aria-hidden />
      Auto
    </span>
  );
}

// ── Enrich-refresh button tuning (ea/enrich-refresh-button) ───────────────────────────────

/** How often the button polls firms.get() while an enrichment run is in flight. */
const ENRICH_POLL_INTERVAL_MS = 1500;
/** Give up polling after this long and show a "still running" / timeout message. */
const ENRICH_POLL_TIMEOUT_MS = 15_000;
/**
 * Client-side mirror of the server's per-firm cooldown (FIRM_ENRICH_COOLDOWN_MS in
 * apps/api/src/modules/firms/firm-enrich.rate-limiter.ts) — started the moment the POST is
 * accepted, same as when the server stamps its own cooldown key, so the two stay in sync.
 */
const ENRICH_CLIENT_COOLDOWN_MS = 30_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** The Stammdaten fields the register enrichment can actually write (hold()-guarded on the
 * server by manual_overrides) — the set the diff-toast compares before vs. after a run.
 * Deliberately excludes phone/annual_revenue/customer_since/etc: those are pure-manual (M)
 * fields enrichment never touches, so diffing them would only ever be noise. */
const ENRICH_DIFF_FIELDS: Array<{ key: keyof FirmDetail; label: string }> = [
  { key: 'name', label: 'Firmenname' },
  { key: 'legal_form', label: 'Rechtsform' },
  { key: 'uid', label: 'UID' },
  { key: 'address', label: 'Adresse' },
  { key: 'postcode', label: 'PLZ' },
  { key: 'city', label: 'Ort' },
  { key: 'canton', label: 'Kanton' },
  { key: 'noga_code', label: 'NOGA-Code' },
  { key: 'branche', label: 'Branche' },
  { key: 'founding_year', label: 'Gründungsjahr' },
  { key: 'business_activity', label: 'Geschäftstätigkeit' },
  { key: 'vat_number', label: 'MwSt-Nummer' },
  { key: 'mwst_pflichtig', label: 'MwSt-pflichtig' },
];

/** null/undefined/'' all compare equal (mirrors the Row component's own "empty" treatment). */
function normalizeForDiff(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v).trim();
}

/** The labels of every ENRICH_DIFF_FIELDS entry that changed between two firm-detail reads. */
function diffEnrichedFields(before: FirmDetail, after: FirmDetail): string[] {
  return ENRICH_DIFF_FIELDS.filter((f) => {
    const beforeValue = before[f.key] as string | number | boolean | null | undefined;
    const afterValue = after[f.key] as string | number | boolean | null | undefined;
    return normalizeForDiff(beforeValue) !== normalizeForDiff(afterValue);
  }).map((f) => f.label);
}

function Row({
  label,
  value,
  auto = false,
}: {
  label: string;
  value: string | null | undefined;
  /** P2-8: true for the auto-derived fields (§3.2) — shows the small "Auto" badge next to the label. */
  auto?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 py-1.5 text-sm">
      {/* G1 fix: the "Auto" badge sits on its own line directly under the label instead of
          sharing one row with it. A label that wraps to two lines (Vorsteuerabzugsberechtigt,
          MwSt-Nummer, MwSt-pflichtig) used to leave the badge vertically centered against BOTH
          lines, landing on top of the label text (or, with `items-baseline` on the outer row,
          drifting down into the value column for long values like Geschäftstätigkeit). Stacking
          label + badge, and switching the row to top-alignment, removes the overlap for every
          row, auto or not, regardless of how many lines the label wraps to.
      */}
      <div className="flex w-32 shrink-0 flex-col gap-1 text-xs text-muted-foreground">
        <span>{label}</span>
        {auto && <AutoBadge />}
      </div>
      {/* A hyphen rather than an empty cell, so a missing field reads as "not filled in"
          rather than as a rendering bug. */}
      <span className="min-w-0 flex-1 break-words">{value?.trim() ? value : '-'}</span>
    </div>
  );
}

export function FirmenStammdaten({
  firmaId,
  canEdit = false,
  /** Bumped by the parent to force a refetch after an edit made elsewhere (the action menu). */
  refreshKey,
  /** Called after a successful inline save so the parent can refresh the header/name. */
  onSaved,
  /**
   * Called after a successful MANUAL enrichment run settles (zefix_status 'done'). The parent
   * uses this to refresh siblings that read register-derived data (e.g. the Zeichnungsberechtigte
   * card), which would otherwise show stale values until the next full load.
   */
  onEnriched,
}: {
  firmaId: string;
  canEdit?: boolean;
  refreshKey?: number;
  onSaved?: (updated: FirmDetailWire) => void;
  onEnriched?: () => void;
}) {
  const [data, setData] = useState<FirmDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── D1-14 inline edit state ─────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FirmDraft | null>(null);
  const [original, setOriginal] = useState<FirmDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── ea/enrich-refresh-button: manual re-fetch state ──────────────────────────
  const [enrichRunning, setEnrichRunning] = useState(false);
  const [enrichCooldownUntil, setEnrichCooldownUntil] = useState<number | null>(null);
  const [enrichBlacklisted, setEnrichBlacklisted] = useState(false);
  const [enrichMessage, setEnrichMessage] = useState<
    { kind: 'success' | 'error' | 'info'; text: string } | null
  >(null);
  const [enrichLastTriggeredAt, setEnrichLastTriggeredAt] = useState<number | null>(null);
  // Ticks once/second ONLY while a cooldown is active, to live-update the countdown below.
  const [enrichNowTick, setEnrichNowTick] = useState(() => Date.now());
  // Guards state updates from an in-flight click/poll after the component unmounts or the
  // caller navigates to a different firm (mirrors the `cancelled` local in the load effect
  // above, but needs to survive across the async click handler, hence a ref).
  const enrichCancelledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.firms
      .get(firmaId)
      .then((res) => {
        if (!cancelled) {
          setData(res as FirmDetail);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Die Stammdaten konnten nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmaId, refreshKey]);

  // A new firm (or an externally-triggered refetch) invalidates any in-flight enrich run and
  // resets the button's cooldown/blacklist/message state — none of it applies to the new data.
  useEffect(() => {
    enrichCancelledRef.current = false;
    setEnrichRunning(false);
    setEnrichCooldownUntil(null);
    setEnrichBlacklisted(false);
    setEnrichMessage(null);
    setEnrichLastTriggeredAt(null);
    return () => {
      enrichCancelledRef.current = true;
    };
  }, [firmaId, refreshKey]);

  // Live countdown while cooling down — the ONLY thing that needs a ticking clock here.
  useEffect(() => {
    if (enrichCooldownUntil === null) return;
    const id = setInterval(() => setEnrichNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enrichCooldownUntil]);

  const enrichCooldownRemaining =
    enrichCooldownUntil !== null ? Math.max(0, Math.ceil((enrichCooldownUntil - enrichNowTick) / 1000)) : 0;

  useEffect(() => {
    if (enrichCooldownUntil !== null && Date.now() >= enrichCooldownUntil) {
      setEnrichCooldownUntil(null);
    }
  }, [enrichNowTick, enrichCooldownUntil]);

  const ort = data
    ? [data.postcode, data.city].filter((v) => v?.trim()).join(' ') || null
    : null;

  function startEdit() {
    if (!data || data.restricted === true) return;
    const d = draftFromWire(data);
    setOriginal(d);
    setDraft(d);
    setSaveError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(null);
    setOriginal(null);
    setSaveError(null);
  }

  function onField(field: keyof FirmDraft, value: string) {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await apiClient.firms.update(firmaId, draftToPatch(draft));
      setData(updated);
      onSaved?.(updated);
      cancelEdit();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Die Stammdaten konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  /**
   * The refresh/sync button: trigger a manual re-fetch, then poll firms.get() until the
   * enrichment settles (or times out), diff the enrichment-touched fields against the
   * pre-click snapshot, and report what changed.
   */
  async function handleEnrich() {
    if (!data || data.restricted === true || enrichRunning || enrichBlacklisted) return;
    if (enrichCooldownRemaining > 0) return;

    const snapshot = data;
    setEnrichMessage(null);
    setEnrichRunning(true);

    try {
      await apiClient.firms.enrich(firmaId);
      if (enrichCancelledRef.current) return;

      // The server stamps its per-firm cooldown key the moment it accepts the trigger — mirror
      // that timing client-side so the two cooldowns stay in sync (see ENRICH_CLIENT_COOLDOWN_MS).
      const triggeredAt = Date.now();
      setEnrichLastTriggeredAt(triggeredAt);
      setEnrichCooldownUntil(triggeredAt + ENRICH_CLIENT_COOLDOWN_MS);

      // ── Poll until zefix_status settles (done/failed) or the timeout elapses ────────────
      const pollDeadline = Date.now() + ENRICH_POLL_TIMEOUT_MS;
      let latest: FirmDetail | null = null;
      let settled: 'done' | 'failed' | null = null;

      while (Date.now() < pollDeadline) {
        await sleep(ENRICH_POLL_INTERVAL_MS);
        if (enrichCancelledRef.current) return;

        latest = (await apiClient.firms.get(firmaId)) as FirmDetail;
        if (enrichCancelledRef.current) return;

        const status = latest.zefix_status;
        if (status === 'done' || status === 'failed') {
          settled = status;
          break;
        }
      }

      if (latest) setData(latest);

      if (settled === 'done' && latest) {
        const changed = diffEnrichedFields(snapshot, latest);
        setEnrichMessage(
          changed.length > 0
            ? { kind: 'success', text: `Stammdaten aktualisiert: ${changed.join(', ')}` }
            : { kind: 'info', text: 'Keine neuen Daten gefunden.' },
        );
        // Register-derived siblings (Zeichnungsberechtigte) may now be stale — tell the parent
        // to refetch them. Fire on a settled run, not only when Stammdaten fields changed, since
        // the register can update signatories without touching the diffed Stammdaten fields.
        onEnriched?.();
      } else if (settled === 'failed') {
        setEnrichMessage({
          kind: 'error',
          text: 'Kein Registereintrag gefunden / Abgleich fehlgeschlagen.',
        });
      } else {
        // Timed out without a terminal status. Still actively running → say so; otherwise
        // (back at 'pending', the in-process queue's no-retry reset) treat it the same as a
        // failed match — that IS what a reset-to-pending means in this build.
        setEnrichMessage(
          latest?.zefix_status === 'in_progress'
            ? { kind: 'info', text: 'Abgleich läuft noch, bitte in Kürze erneut prüfen.' }
            : { kind: 'error', text: 'Kein Registereintrag gefunden / Abgleich fehlgeschlagen.' },
        );
      }
    } catch (err: unknown) {
      if (enrichCancelledRef.current) return;

      if (err instanceof ApiError && err.status === 422) {
        setEnrichBlacklisted(true);
        setEnrichMessage({
          kind: 'error',
          text: 'Anreicherung für eine gesperrte Firma nicht möglich.',
        });
      } else if (err instanceof ApiError && err.status === 429) {
        const retryAfter = err.retryAfterSeconds ?? 30;
        setEnrichCooldownUntil(Date.now() + retryAfter * 1000);
        setEnrichMessage({
          kind: 'info',
          text:
            err.code === 'enrich_in_progress'
              ? 'Abgleich läuft bereits.'
              : `Zu viele Anfragen, bitte in ${retryAfter}s erneut versuchen.`,
        });
      } else {
        setEnrichMessage({
          kind: 'error',
          text: err instanceof Error ? err.message : 'Abgleich fehlgeschlagen.',
        });
      }
    } finally {
      if (!enrichCancelledRef.current) setEnrichRunning(false);
    }
  }

  const enrichTitle = enrichBlacklisted
    ? 'Anreicherung für gesperrte Firma nicht möglich'
    : enrichRunning
      ? 'Abgleich läuft …'
      : enrichCooldownRemaining > 0
        ? `Erneut möglich in ${enrichCooldownRemaining}s`
        : enrichLastTriggeredAt !== null
          ? `Zuletzt abgeglichen um ${new Date(enrichLastTriggeredAt).toLocaleTimeString('de-CH', {
              hour: '2-digit',
              minute: '2-digit',
            })}`
          : 'Firmenstammdaten neu abgleichen';

  const nameEmpty = draft !== null && draft.name.trim() === '';
  // P2-8: reference_currency is NOT NULL at the DB, so — like the name — an empty/invalid
  // value must block Save rather than silently round-trip as an empty string.
  const currencyInvalid = draft !== null && referenceCurrencyInvalid(draft);
  // P2-8 (Fix-B/W-F2): a non-numeric Jahresumsatz must block Save, not round-trip into a 400/500.
  const revenueInvalid = draft !== null && annualRevenueInvalid(draft);
  const noChanges = original !== null && draft !== null && draftUnchanged(original, draft);
  const editable = canEdit && data !== null && data.restricted !== true && !loading;

  return (
    <section className="rounded-lg border bg-card" aria-label="Firmenstammdaten">
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Firmenstammdaten</h3>
        <div className="flex items-center gap-2">
          {!editing && data?.restricted !== true && data !== null && (
            <button
              type="button"
              onClick={() => void handleEnrich()}
              disabled={enrichRunning || enrichBlacklisted || enrichCooldownRemaining > 0}
              aria-label="Firmenstammdaten neu abgleichen"
              title={enrichTitle}
              className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${enrichRunning ? 'animate-spin' : ''}`} />
            </button>
          )}
          {!editing && data?.restricted !== true && data !== null && enrichCooldownRemaining > 0 && !enrichRunning && (
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {enrichCooldownRemaining}s
            </span>
          )}
          {!editing && data?.restricted !== true && data !== null && (
            // The D1 payoff: say whether these fields came from the register or are simply
            // not looked up yet. Empty fields alone cannot distinguish the two.
            <span className="text-xs text-muted-foreground">
              {enrichmentLabel(data.zefix_status)}
            </span>
          )}
          {!editing && editable && (
            <button
              type="button"
              onClick={startEdit}
              aria-label="Firmenstammdaten bearbeiten"
              className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <Pencil className="size-3.5" />
            </button>
          )}
          {editing && (
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              aria-label="Bearbeiten abbrechen"
              className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </header>

      <div className="px-4 py-2">
        {enrichMessage !== null && (
          <p
            className={`flex items-start gap-2 py-2 text-sm ${
              enrichMessage.kind === 'error'
                ? 'text-destructive'
                : enrichMessage.kind === 'success'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-muted-foreground'
            }`}
          >
            {enrichMessage.kind === 'error' ? (
              <AlertCircle className="size-4 shrink-0" />
            ) : enrichMessage.kind === 'success' ? (
              <CheckCircle2 className="size-4 shrink-0" />
            ) : (
              <Info className="size-4 shrink-0" />
            )}
            {enrichMessage.text}
          </p>
        )}

        {loading && (
          <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Wird geladen …
          </p>
        )}

        {error !== null && (
          <p className="flex items-start gap-2 py-2 text-sm text-destructive">
            <AlertCircle className="size-4 shrink-0" />
            {error}
          </p>
        )}

        {!loading && data?.restricted === true && (
          // "You may not see this" is a different fact from "this is not filled in", and the
          // card says which. The api already refuses to send the fields.
          <p className="flex items-start gap-2 py-2 text-sm text-muted-foreground">
            <ShieldOff className="size-4 shrink-0" />
            Dieser Kunde ist gesperrt. Die Stammdaten sind für Sie nicht sichtbar.
          </p>
        )}

        {/* Read mode */}
        {!loading && !editing && data !== null && data.restricted !== true && (
          <div className="flex flex-col divide-y">
            <Row label="Firmenname" value={data.name} />
            <Row
              label="Rechtsform"
              value={data.legal_form ? (LEGAL_FORM_LABEL[data.legal_form] ?? data.legal_form) : null}
            />
            <Row label="UID" value={data.uid} />
            <Row label="Adresse" value={data.address} />
            <Row label="PLZ / Ort" value={ort} />
            <Row label="Kanton" value={data.canton} />
            <Row label="Telefon" value={data.phone} />

            {/* P2-8 (§3.2) auto-derived group — same fields the enrichment worker fills,
                override-protected once a broker edits them (D-14's manual_overrides). */}
            <Row label="NOGA-Code" value={data.noga_code} auto />
            <Row label="Branche" value={data.branche} auto />
            <Row
              label="Gründungsjahr"
              value={data.founding_year != null ? String(data.founding_year) : null}
              auto
            />
            <Row label="Geschäftstätigkeit" value={data.business_activity} auto />
            <Row label="MwSt-Nummer" value={data.vat_number} auto />
            <Row label="MwSt-pflichtig" value={triStateLabel(data.mwst_pflichtig)} auto />

            {/* P2-8 pure-manual group — never written by enrichment, broker-entered only. */}
            <Row
              label="Jahresumsatz"
              value={formatMoney(data.annual_revenue, data.reference_currency ?? 'CHF')}
            />
            <Row label="Referenzwährung" value={data.reference_currency} />
            <Row label="Kunde seit" value={dateShortDe(data.customer_since) || null} />
            <Row
              label="Vorsteuerabzugsberechtigt"
              value={triStateLabel(data.input_tax_deductible)}
            />
            <Row label="Adresszusatz" value={data.address_supplement} />
          </div>
        )}

        {/* Edit mode (D1-14) */}
        {editing && draft !== null && (
          <div className="flex flex-col gap-3 py-2">
            <FirmStammdatenForm draft={draft} onField={onField} disabled={saving} />

            {saveError !== null && (
              <p className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="size-4 shrink-0" />
                {saveError}
              </p>
            )}
            {nameEmpty && (
              <p className="text-xs text-destructive">Der Firmenname darf nicht leer sein.</p>
            )}
            {currencyInvalid && (
              <p className="text-xs text-destructive">
                Die Referenzwährung muss ein gültiger 3-stelliger Code sein (z. B. CHF).
              </p>
            )}

            <p className="text-xs text-muted-foreground">
              Von Hand geänderte Felder werden beim nächsten Handelsregister-Abgleich nicht
              überschrieben.
            </p>

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={cancelEdit} disabled={saving}>
                Abbrechen
              </Button>
              <Button
                onClick={() => void save()}
                disabled={saving || nameEmpty || currencyInvalid || revenueInvalid || noChanges}
              >
                {saving ? 'Wird gespeichert …' : 'Speichern'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
